const state = reactive({
  isFetching: false,
  userProfiles: {} as Record<string, UserProfile>,
});

export const useUserCache = () => {
  const { $gfalIdApi } = useNuxtApp();
  const { isFetching, userProfiles } = toRefs(state);
  const { userCacheExpirationMinutes } = useRuntimeConfig().public;
  const userStore = useUserStore();
  const userIds = ref(new Set<string>());

  const addUserId = (id: string) => {
    userIds.value.add(id);
  };

  const addUserIds = async (ids: string[]) => {
    for (const id of ids) {
      userIds.value.add(id);
    }
    await loadUserProfiles();
  };

  const loadUserProfiles = async () => {
    isFetching.value = true;

    const missingOrExpiredUserIds = Array.from(userIds.value).filter(
      (id) =>
        !userProfiles.value[id] || isExpired(userProfiles.value[id].cachedTime)
    );

    if (missingOrExpiredUserIds.length > 0) {
      await batchFetchUserProfiles(missingOrExpiredUserIds).catch(() => {});
    }
    isFetching.value = false;
  };

  const isExpired = (cachedTime: string) => {
    if (
      Number(userCacheExpirationMinutes) === 0 ||
      !userCacheExpirationMinutes
    ) {
      return true;
    }

    const expirationTimeInMilliseconds =
      Number(userCacheExpirationMinutes) * 1000 * 60;
    return (
      new Date(cachedTime).getTime() + expirationTimeInMilliseconds <
      new Date().getTime()
    );
  };

  const batchFetchUserProfiles = async (ids: string[]) => {
    const response = await $gfalIdApi<GFALResponse<UserProfile[]>>(
      `/api/users`,
      {
        headers: constructHeaders(userStore.authToken),
        query: {
          id: ids.join(","),
        },
      }
    );
    if (response.payload) {
      for (const profile of response.payload) {
        profile.cachedTime = new Date().toISOString();
        userProfiles.value[profile.id] = profile;
      }
    }
  };

  const getUserProfileFromCache = (id: string) => {
    if (!!userIds.value.has(id)) {
      return null;
    }
    return userProfiles.value[id];
  };

  const clearUserProfiles = () => {
    userIds.value.clear();
    userProfiles.value = {};
  };

  return {
    isFetching,
    addUserId,
    addUserIds,
    loadUserProfiles,
    getUserProfileFromCache,
    clearUserProfiles,
  };
};
